import { useState, useEffect } from 'react';

import { SortEnum } from '@/fo/api/magento';
import { Product, QuestionsData, SortType } from '@/fo/ui/review/catalog/base';

import { getQuestions } from '../api/getQuestions';

const INITIAL_PAGE = 1;

export function useQuestions(
  product: Product,
  initialQuestionsData: QuestionsData
) {
  const [questionsData, setQuestionsData] = useState(initialQuestionsData);
  const [questionsPage, setQuestionsPage] = useState(INITIAL_PAGE);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsSortOrder, setQuestionsSortOrder] = useState<SortEnum>(
    SortEnum.Desc
  );

  const fetchInitialQuestions = async () => {
    const questions = await getQuestions(
      product.id,
      questionsPage,
      questionsSortOrder
    );

    if (questions.error) {
      return;
    }

    setQuestionsData(questions.data);
  };

  useEffect(() => {
    fetchInitialQuestions();
  }, []);

  const fetchMoreQuestions = async () => {
    setQuestionsLoading(true);

    const questionsData = await getQuestions(
      product.id,
      questionsPage + 1,
      questionsSortOrder
    );

    const questions = questionsData.data;

    setQuestionsData((prev) => {
      const allItems = [...prev.items, ...questions.items];
      const uniqueMap = new Map(allItems.map((item) => [item.id, item]));
      const uniqueItems = Array.from(uniqueMap.values());

      return {
        ...questions,
        items: uniqueItems
      };
    });

    setQuestionsPage(questionsPage + 1);
    setQuestionsLoading(false);
  };

  const handleSortOrderChange = async (sortType: SortType | null) => {
    const sortOrderMap = {
      [SortType.Oldest]: SortEnum.Asc,
      [SortType.Newest]: SortEnum.Desc
    };

    const sortOrder = sortOrderMap[sortType ?? SortType.Newest];

    if (questionsSortOrder === sortOrder) {
      return;
    }

    setQuestionsSortOrder(sortOrder);
    setQuestionsPage(INITIAL_PAGE);
    setQuestionsLoading(true);

    const questions = await getQuestions(product.id, INITIAL_PAGE, sortOrder);
    setQuestionsData(questions.data);
    setQuestionsLoading(false);
  };

  return {
    questionsData,
    questionsLoading,
    questionsSortOrder,
    fetchMoreQuestions,
    handleSortOrderChange
  };
}
